// frontend/src/utils/industrySuggestion.ts
import type { CompanyEnrichmentResponse, IndustrySearchResult } from '../services/api';

/**
 * Extract search queries from AI enrichment output.
 *
 * We trust the LLM to provide accurate industry_keywords and use those as our primary signal.
 * We avoid hardcoded industry biases to ensure this works across all sectors (B2B, industrial, consumer, etc.)
 */
export function buildIndustrySearchQueriesFromEnrichment(
  enrichment: CompanyEnrichmentResponse | null
): string[] {
  if (!enrichment) return [];

  const queries: string[] = [];

  const push = (value?: string | null) => {
    const v = (value || '').trim();
    if (!v) return;
    queries.push(v);
  };

  // Primary signal: Use explicit keywords from the LLM
  // The LLM has full context and should provide accurate industry terms
  for (const kw of enrichment.structured_data?.industry_keywords || []) {
    push(kw);
  }

  // Fallback: If no keywords provided, try extracting key phrases from the description
  if (queries.length === 0) {
    const desc = (enrichment.structured_data?.company_description || '').toLowerCase();
    
    // Only add very generic, high-signal terms as fallbacks
    if (desc.includes('mining') || desc.includes('mineral')) push('mining');
    if (desc.includes('metal') && desc.includes('processing')) push('metals processing');
    if (desc.includes('equipment') && desc.includes('industrial')) push('industrial equipment');
    if (desc.includes('machinery')) push('machinery');
    if (desc.includes('cosmetic') || desc.includes('beauty')) push('cosmetics');
    if (desc.includes('personal care')) push('personal care');
    if (desc.includes('household') || desc.includes('cleaning')) push('household products');
    if (desc.includes('food') && (desc.includes('processing') || desc.includes('manufacturer'))) push('food products');
    if (desc.includes('pharmaceutical') || desc.includes('medicine')) push('pharmaceuticals');
    if (desc.includes('software') || desc.includes('saas')) push('software');
    if (desc.includes('semiconductor') || desc.includes('chip')) push('semiconductors');
  }

  // De-duplicate while preserving order
  const seen = new Set<string>();
  const unique = queries
    .map(q => q.trim())
    .filter(q => q.length >= 2)
    .filter(q => {
      const key = q.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return unique.slice(0, 8); // keep it small
}

/**
 * Score a search result against an enrichment payload.
 * Higher score = better fit.
 * 
 * This scoring is now generic and works across all industries.
 */
export function scoreIndustryResultFromEnrichment(
  result: IndustrySearchResult,
  enrichment: CompanyEnrichmentResponse
): number {
  const text = [
    result.level01.name,
    result.level02.name,
    result.level03.name,
    result.level04.name,
    result.level04.detail || '',
    result.display,
  ]
    .join(' ')
    .toLowerCase();

  const keywords = (enrichment.structured_data?.industry_keywords || [])
    .map(k => (k || '').toLowerCase().trim())
    .filter(Boolean);

  const profileText = (enrichment.full_profile || '').toLowerCase();

  let score = 0;

  // Primary scoring: keyword matching
  // Each matching keyword adds to the score
  for (const kw of keywords) {
    if (!kw || kw.length < 3) continue;
    
    // Higher weight for matches in the detail field (most specific)
    if ((result.level04.detail || '').toLowerCase().includes(kw)) score += 3;
    // Medium weight for matches in level 4 name
    else if (result.level04.name.toLowerCase().includes(kw)) score += 2;
    // Lower weight for matches anywhere else
    else if (text.includes(kw)) score += 1;
  }

  // Secondary scoring: general text overlap
  // Split the company description into meaningful words
  const descWords = (enrichment.structured_data?.company_description || '')
    .toLowerCase()
    .split(/\s+/)
    .filter(w => w.length > 4); // Only consider substantial words

  for (const word of descWords.slice(0, 10)) { // Top 10 words only
    if (text.includes(word)) score += 0.5;
  }

  return score;
}


